import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { useTranslation } from '../../../utils/translation';
import { useImageComponent, useLinkComponent } from '../../../utils/component';
import { Cta } from '../../cta';
import type { LinkProps } from '../../../atoms/types';
import { ProsAndCons } from '../../pros-and-cons';
import { StarsMark } from '../../stars-with-mark';
import { DetailedStickerType, MainCtaInfos } from '../../types';
import DetailedSticker from '../../../icons/sticker/detailedSticker';

export type MiniCardProps = {
  id?: number;
  size?: number;
  label?: string;
  sticker?: string;
  image: { imageId: string; alt: string; extension?: string; title?: string };
  title?: string;
  rating?: number;
  userRating?: number;
  pros?: any;
  productLink?: LinkProps;
  ctaPropsCard?: LinkProps;
  testLink?: LinkProps;
  mainCtaInfos?: MainCtaInfos;
  imageRatio?: string;
  detailedSticker?: DetailedStickerType;
};

const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--color-alt-4);
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  padding: 50px 30px 30px 30px;
  position: relative;
  top: 0;
  transition: top 0.3s;

  &.mod-selected {
    border: solid 3px var(--theme-color-primary);
  }

  &.mod-without-sticker {
    padding: 30px;
  }

  &:hover {
    top: -3px;
  }

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
      flex-flow: wrap;
      margin: 15px 0;
      gap: 20px 0;
      padding: 60px 15px 15px;
    `,
  )}
`;

const Sticker = styled.div`
  background: ${(props) => (props.theme.isDark ? '#777777' : props.theme.palette.blueMedium)};
  border-radius: 8px 0 10px 0;
  color: white;
  font-size: 16px;
  font-weight: 700;
  left: 0;
  padding: 5px 15px;
  position: absolute;
  top: 0;

  .mod-selected & {
    background: var(--theme-color-primary);
    left: -1px;
    top: -1px;
  }

  a {
    color: white;
  }
`;

const ImageWrapper = styled.div`
  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      order: 1;
    `,
  )}
`;

const Title = styled.div`
  font-family: var(--theme-typography-primary-font);
  font-size: 22px;
  font-weight: bold;
  margin: 15px 0;

  a {
    color: var(--title-foreground);
    text-decoration: underline;
    transition: color 0.3s;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 54px;

    &:hover {
      color: var(--theme-color-primary);
    }
  }

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 0;
    `,
  )}
`;

const MobileTitle = styled(Title)`
  display: block;
  width: 100%;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const DesktopTitle = styled(Title)`
  display: none;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: block;
    `,
  )}
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: auto;
  padding-top: 30px;

  > * {
    justify-content: center;
  }

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: column-reverse;
      margin: 0 0 0 auto;
      order: 2;
      padding: 0;
      width: auto;
    `,
  )}
`;

const ProsAndConsWrapper = styled.div`
  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      order: 3;
      width: 100%;
    `,
  )}
`;

const RefurbishedCtaContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const RefurbishedLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 10px;
  gap: 3px;
`;

const RefurbishedLabel = styled.div`
  font-size: 12px;
  line-height: 12px;
`;

const ShopIcon = styled.img`
  height: 24px;
  width: 24px;
`;

const WaitingShopIcon = styled.div`
  height: 24px;
  width: 24px;
`;

const AwardContainer = styled.div`
  position: absolute;
  top: calc(-0.7 * var(--spacer-m));
  right: var(--spacer-s);
  z-index: 9;
  padding: initial;
  margin: initial;
`;

const getDefaultLabel = (
  layout: string,
  sponsored: boolean,
  internalLink: boolean,
  defaultLabels: { [key: string]: string },
): string => {
  const correspondingKey = Object.keys(defaultLabels).find((objectKey) => {
    return objectKey === layout;
  });

  if (correspondingKey) {
    if (!internalLink && sponsored) {
      return defaultLabels.external;
    }

    return defaultLabels[correspondingKey];
  }

  if (!internalLink && sponsored) {
    return defaultLabels.external;
  }

  return defaultLabels.default;
};

const CtaMiniCard = ({
  layout,
  shopName,
  internalLink,
  sponsored,
  icon,
  label,
  refurbished,
  ctaPropsCard,
}: MainCtaInfos) => {
  const [lazyShopLogo, setLazyShopLogo] = useState(false);
  const t = useTranslation();

  const defaultLabels = {
    default: t('design.miniCard.defaultLabels.default'),
    product: t('design.miniCard.defaultLabels.product'),
    service: t('design.miniCard.defaultLabels.online_service'),
    download: t('design.miniCard.defaultLabels.download'),
    external: t('design.miniCard.defaultLabels.external'),
  };

  const defaultLabel = getDefaultLabel(layout, sponsored, internalLink, defaultLabels);

  useEffect(() => {
    if (!lazyShopLogo) {
      setLazyShopLogo(true);
    }
  }, [lazyShopLogo, setLazyShopLogo]);

  if (refurbished) {
    return (
      <Cta {...ctaPropsCard}>
        <RefurbishedCtaContainer>
          {icon && lazyShopLogo && <ShopIcon src={icon} alt={shopName || ''} />}
          {icon && !lazyShopLogo && <WaitingShopIcon />}
          <RefurbishedLabelContainer>
            {label || defaultLabel}
            <RefurbishedLabel>{t('design.refurbished')}</RefurbishedLabel>
          </RefurbishedLabelContainer>
        </RefurbishedCtaContainer>
      </Cta>
    );
  }

  return (
    <Cta {...ctaPropsCard}>
      <RefurbishedCtaContainer>
        {icon && lazyShopLogo && <ShopIcon src={icon} alt={shopName || ''} />}
        {icon && !lazyShopLogo && <WaitingShopIcon />}
        {label || defaultLabel}
      </RefurbishedCtaContainer>
    </Cta>
  );
};

const MiniCard = ({
  title,
  pros,
  image,
  rating,
  userRating,
  productLink,
  sticker,
  mainCtaInfos,
  imageRatio,
  detailedSticker,
}: MiniCardProps) => {
  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  const imageProps = {
    ...image,
    mobileWidth: imageRatio === 'portrait' ? 73 : 90,
    mobileHeight: imageRatio === 'landscape' ? 73 : 90,
    desktopWidth: imageRatio === 'portrait' ? 147 : 180,
    desktopHeight: imageRatio === 'landscape' ? 147 : 180,
  };

  let globalRating = rating;
  if (rating === 0 && userRating > 0) {
    globalRating = userRating;
  }

  return (
    <Wrapper className={sticker ? '' : 'mod-without-sticker'}>
      {detailedSticker && (
        <AwardContainer>
          <DetailedSticker detailedSticker={detailedSticker} scale={0.17} />
        </AwardContainer>
      )}
      {sticker && <Sticker>{sticker} </Sticker>}
      <MobileTitle>{title}</MobileTitle>
      <ImageWrapper>
        <ImageComponent {...imageProps} />
      </ImageWrapper>
      <DesktopTitle>
        <LinkComponent {...productLink}>{title}</LinkComponent>
      </DesktopTitle>
      <ProsAndConsWrapper>
        <ProsAndCons pros={pros?.slice(0, 3)} hideTitle />
      </ProsAndConsWrapper>
      <Bottom>
        <CtaMiniCard {...mainCtaInfos} />
        {globalRating && globalRating > 0 ? (
          <StarsMark max={5} rating={globalRating / 2} maxLabel={10} ratingLabel={globalRating} />
        ) : null}
      </Bottom>
    </Wrapper>
  );
};

export default MiniCard;
